import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader, AlertCircle } from 'lucide-react';
import { config } from '../utils/config';

interface Message {
  role: 'assistant' | 'user';
  content: string;
  error?: boolean;
}

const AIPage: React.FC = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setMessages(prev => [...prev, { role: 'user', content: input }]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${config.API_URL}${config.endpoints.ai}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: input }),
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Authentication required');
        }
        
        const errorText = await response.text();
        let errorMessage = 'Server error';
        
        try {
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      
      if (responseText && responseText.trim()) {
        try {
          const data = JSON.parse(responseText);
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: data.response 
          }]);
        } catch (parseError) {
          console.error('Error parsing AI response:', parseError);
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Empty response from AI service');
      }

    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error processing request. Please try again.',
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-8rem)]">
      {error && (
        <div className="m-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-200">
          <AlertCircle className="h-4 w-4" />
          <p>{error}</p>
        </div>
      )}

      {/* Chat Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex items-start gap-3 ${
              message.role === 'assistant' 
                ? message.error 
                  ? 'bg-red-500/10' 
                  : 'bg-purple-500/10' 
                : 'bg-black/20'
            } p-4 rounded-lg backdrop-blur-sm transition-all duration-200`}
          >
            <div className={`w-8 h-8 rounded-lg ${
              message.error ? 'bg-red-500/20' : 'bg-purple-500/20'
            } flex items-center justify-center shrink-0`}>
              {message.role === 'assistant' ? (
                <Bot className={`w-5 h-5 ${
                  message.error ? 'text-red-400' : 'text-purple-400'
                }`} />
              ) : (
                <User className="w-5 h-5 text-purple-400" />
              )}
            </div>
            <div className={`flex-1 text-sm whitespace-pre-wrap ${
              message.error ? 'text-red-100' : 'text-purple-100'
            }`}>
              {message.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
        
        {loading && (
          <div className="flex items-center gap-2 text-purple-400">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Go grab some water while I'm thinking...</span>
          </div>
        )}
      </div>

      {/* Input Form */}
      <div className="p-4 border-t border-purple-500/20 bg-black/20 backdrop-blur-sm">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex gap-4">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Feeling chatty?"
              className="flex-1 bg-purple-500/10 border border-purple-500/20 rounded-lg px-4 py-2 text-purple-100 placeholder-purple-400/60 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 px-4 py-2 rounded-lg font-medium border border-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors duration-200"
            >
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {loading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIPage;